import { PendingOperation, UpdateState, UpdateAction } from '../types/characterUpdates';
import { CharacterData } from '../types/character';

// Generate unique operation ID
export const generateOperationId = (): string => {
  return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Create a complete pending operation with metadata
export const createPendingOperation = (
  operationTemplate: Omit<PendingOperation, 'id' | 'timestamp' | 'retryCount'>
): PendingOperation => ({
  ...operationTemplate,
  id: generateOperationId(),
  timestamp: Date.now(),
  retryCount: 0
});

// Apply all pending operations to base character to get optimistic state
export const applyPendingOperations = (
  baseCharacter: CharacterData | null,
  pendingOperations: PendingOperation[]
): CharacterData | null => {
  if (!baseCharacter) return null;
  
  return pendingOperations.reduce(
    (character, operation) => {
      try {
        return operation.operation(character);
      } catch (error) {
        console.error(`Failed to apply operation ${operation.id}:`, error);
        // Return character unchanged on operation failure
        return character;
      }
    },
    baseCharacter
  );
};

// Reducer for managing optimistic state
export const updateReducer = (
  state: UpdateState,
  action: UpdateAction
): UpdateState => {
  switch (action.type) {
    case 'SET_BASE_CHARACTER':
      return {
        ...state,
        baseCharacter: action.character,
        loading: false,
        error: null
      };

    case 'QUEUE_OPERATION':
      console.log(`Queuing operation: ${action.operation.type} (${action.operation.id})`);
      return {
        ...state,
        pendingOperations: [...state.pendingOperations, action.operation],
        error: null // Clear any previous errors when new operation is queued
      };

    case 'OPERATIONS_SAVED':
      console.log(`Operations saved successfully: ${action.clearedOperationIds.join(', ')}`);
      return {
        ...state,
        pendingOperations: state.pendingOperations.filter(
          op => !action.clearedOperationIds.includes(op.id)
        ),
        saving: false,
        lastSavedVersion: Date.now(),
        error: null
      };

    case 'SAVE_FAILED':
      console.error('Save failed:', action.error);
      return {
        ...state,
        saving: false,
        error: action.error
      };

    case 'SET_LOADING':
      return {
        ...state,
        loading: action.loading
      };

    case 'SET_CHARACTERS_LOADING':
      return {
        ...state,
        charactersLoading: action.loading
      };

    case 'SET_CHARACTERS':
      return {
        ...state,
        characters: action.characters,
        charactersLoading: false,
        error: null
      };

    case 'SET_SAVING':
      return {
        ...state,
        saving: action.saving
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };

    case 'RETRY_OPERATION': {
      const updatedOperations = state.pendingOperations.map(op =>
        op.id === action.operationId
          ? { ...op, retryCount: op.retryCount + 1, timestamp: Date.now() }
          : op
      );
      return {
        ...state,
        pendingOperations: updatedOperations,
        error: null
      };
    }

    case 'REMOVE_OPERATION':
      return {
        ...state,
        pendingOperations: state.pendingOperations.filter(op => op.id !== action.operationId)
      };

    default:
      return state;
  }
};

// Batch operations that can be combined
export const batchOperations = (operations: PendingOperation[]): PendingOperation[] => {
  // Group operations by type for potential batching
  const grouped = new Map<string, PendingOperation[]>();
  
  operations.forEach(op => {
    const key = op.type;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(op);
  });

  // For now, return operations as-is (batching can be optimized later)
  // This is where we could implement intelligent batching for similar operations
  return operations;
};

// Check if an operation should be retried
export const shouldRetryOperation = (operation: PendingOperation, maxRetries: number = 3): boolean => {
  return operation.retryCount < maxRetries;
};

// Get operations that are ready for retry (with exponential backoff)
export const getRetryableOperations = (
  operations: PendingOperation[],
  maxRetries: number = 3
): PendingOperation[] => {
  const now = Date.now();
  
  return operations.filter(op => {
    if (op.retryCount === 0) return true; // First attempt
    if (op.retryCount >= maxRetries) return false; // Max retries exceeded
    
    // Exponential backoff: 1s, 2s, 4s, 8s...
    const backoffDelay = Math.pow(2, op.retryCount) * 1000;
    const timeSinceLastAttempt = now - op.timestamp;
    
    return timeSinceLastAttempt >= backoffDelay;
  });
};

// Validate operation before execution
export const validateOperation = (
  operation: PendingOperation,
  character: CharacterData
): { valid: boolean; error?: string } => {
  try {
    switch (operation.type) {
      case 'addEquipment': {
        const result = operation.operation(character);
        if (!result.equipment) {
          return { valid: false, error: 'Operation would corrupt equipment array' };
        }
        break;
      }
      case 'addDomainCardToLoadout':
      case 'moveDomainCardToVault':
      case 'moveDomainCardToLoadout':
      case 'removeDomainCardFromVaultLoadout': {
        const result = operation.operation(character);
        if (!result.inventory?.domainCardLoadout || !result.inventory?.domainCardVault) {
          return { valid: false, error: 'Operation would corrupt vault/loadout system' };
        }
        // Validate loadout limit
        if (result.inventory.domainCardLoadout.length > 5) {
          return { valid: false, error: 'Loadout exceeds 5 card limit' };
        }
        break;
      }
      // Add more validation rules as needed
    }
    
    return { valid: true };
  } catch (error) {
    return { 
      valid: false, 
      error: error instanceof Error ? error.message : 'Operation validation failed' 
    };
  }
};

// Debug utilities
export const logOperationQueue = (operations: PendingOperation[]): void => {
  console.group('📋 Operation Queue Status');
  console.log(`Total operations: ${operations.length}`);
  
  if (operations.length > 0) {
    operations.forEach(op => {
      console.log(`- ${op.type} (${op.id}) - Retry: ${op.retryCount}, Age: ${Date.now() - op.timestamp}ms`);
    });
  } else {
    console.log('Queue is empty');
  }
  
  console.groupEnd();
};

export const getQueueStats = (operations: PendingOperation[]) => {
  const stats = {
    total: operations.length,
    byType: {} as Record<string, number>,
    withRetries: operations.filter(op => op.retryCount > 0).length,
    avgAge: 0
  };

  operations.forEach(op => {
    stats.byType[op.type] = (stats.byType[op.type] || 0) + 1;
  });

  if (operations.length > 0) {
    const totalAge = operations.reduce((sum, op) => sum + (Date.now() - op.timestamp), 0);
    stats.avgAge = totalAge / operations.length;
  }

  return stats;
};